/**
 * AdTypeBadge — a brand-colored chip for a campaign ad / video / partnership type
 * (Instagram Reel, YT Shorts, Stream, TikTok, …). The color is single-sourced from
 * the platform brand palette (`getPartnershipBadgeStyle` → `adTypeToPlatform` →
 * `CHANNEL_PLATFORM_META`), so the same type reads identically on every page.
 *
 * `typeId` is the raw type id used to resolve the color (e.g. "instagram_reel",
 * "stream", "youtube_video"); `label` is the display title. Pass `href` to make the
 * label a link (inherits the badge color). Renders nothing but a muted dash when
 * there is no label.
 */
import { forwardRef } from 'react';
import { getPartnershipBadgeStyle } from '@/lib/constants/partnershipColors';

interface AdTypeBadgeProps {
  typeId?: string | null;
  label?: string | null;
  href?: string;
  className?: string;
}

// forwardRef: callers (e.g. `Tooltip` via Radix `TooltipTrigger asChild`) clone this
// as the trigger element and attach a ref to it — a plain function component would
// warn "Function components cannot be given refs".
const AdTypeBadge = forwardRef<HTMLSpanElement, AdTypeBadgeProps>(function AdTypeBadge(
  { typeId, label, href, className },
  ref
) {
  if (!label || label === '-') return <span className="text-ggc-disabled">-</span>;
  const style = getPartnershipBadgeStyle(typeId ?? '') ?? { backgroundColor: 'var(--ggc-bg-surface)' };
  return (
    <span
      ref={ref}
      className={`inline-block px-1.5 py-0.5 rounded-ggc-sm ggc-text-caption-m${className ? ` ${className}` : ''}`}
      style={style}
    >
      {href ? (
        <a href={href} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit' }}>
          {label}
        </a>
      ) : (
        label
      )}
    </span>
  );
});

export default AdTypeBadge;
